import React from "react";
import { Link } from "react-router-dom";
import SEO, { breadcrumb, faqSchema } from "@/components/SEO";
import { NET_PERSONA_LIST } from "./netData";
import {
  Sparkles, ShieldCheck, Clock, BookOpenCheck, MessageSquareText, FileText,
  Bell, Search, Users, CheckCircle2, ArrowRight, Lock, Landmark, Server,
  UserCheck, Layers, GraduationCap, Zap,
} from "lucide-react";

const NAVY = "#0F2B5B";
const TEAL = "#14B8A6";
const SKY = "#4FC3F7";

const NET_FAQ = [
  { q: "Qu'est-ce que NET ?", a: "NET est le collaborateur numérique souverain développé par MetaConnect. C'est un assistant professionnel — et non un chatbot grand public — intégré à l'écosystème MetaConnect et accessible directement depuis OhMyVue. Il fait gagner du temps aux professionnels de santé sur les tâches numériques, tout en laissant systématiquement les décisions importantes à l'humain." },
  { q: "NET remplace-t-il mon logiciel métier ?", a: "Non. NET ne remplace ni un logiciel métier, ni un dossier patient, ni une messagerie, ni un agenda, ni un ERP ou un CRM. Il vient au-dessus des logiciels existants pour les rendre plus simples à utiliser." },
  { q: "Sur quelle IA repose NET ?", a: "NET repose sur une approche d'IA souveraine : des modèles européens en priorité (Mistral), un hébergement compatible avec les exigences HDS, une architecture respectueuse du RGPD, une authentification professionnelle et une supervision humaine systématique." },
  { q: "NET est-il sécurisé pour les données de santé ?", a: "NET fonctionne dans un environnement sécurisé : il ne remplace pas Pro Santé Connect, respecte les droits utilisateurs, ne contourne jamais les mécanismes de sécurité et limite les informations restituées selon les autorisations de chacun." },
  { q: "À qui s'adresse NET ?", a: "Aux opticiens, audioprothésistes, orthoptistes, ophtalmologues, secrétariats médicaux, établissements de santé, réseaux, groupements, industriels, éditeurs de logiciels et collectivités de santé." },
  { q: "NET peut-il agir sans mon accord ?", a: "Non. La philosophie de NET est claire : NET assiste, l'humain décide. NET prépare, le professionnel valide. Aucune action sensible n'est exécutée sans validation humaine." },
];

const NET_SOFTWARE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "NET — Assistant IA souverain",
  alternateName: "NET Assistant IA",
  applicationCategory: "HealthApplication",
  operatingSystem: "Web",
  url: "https://metaconnect.fr/net",
  image: "https://metaconnect.fr/net-mascotte.png",
  description:
    "NET est le collaborateur numérique souverain de MetaConnect pour les professionnels de santé : réponses métier instantanées, recherche documentaire, synthèses et préparation d'actions à valider. IA européenne (Mistral), hébergement compatible HDS, RGPD.",
  publisher: { "@type": "Organization", name: "MetaConnect", url: "https://metaconnect.fr" },
  audience: { "@type": "Audience", audienceType: "Professionnels de santé : opticiens, audioprothésistes, établissements, groupements, industriels, éditeurs" },
  offers: { "@type": "Offer", priceCurrency: "EUR", availability: "https://schema.org/InStock", url: "https://metaconnect.fr/demo" },
};

const MISSIONS = [
  { icon: Search, t: "Retrouver l'information", d: "Une procédure, une fiche technique, une documentation : NET la retrouve en langage naturel." },
  { icon: MessageSquareText, t: "Répondre aux questions métier", d: "Des réponses construites en priorité sur votre base documentaire maîtrisée." },
  { icon: FileText, t: "Résumer et préparer", d: "Synthèses de documents, préparation de messages, de comptes rendus et de réponses — à valider par vous." },
  { icon: Bell, t: "Rappeler au bon moment", d: "Échéances, notifications MSSanté, agenda : NET signale ce qui compte, quand ça compte." },
  { icon: GraduationCap, t: "Accompagner les équipes", d: "Onboarding des nouveaux collaborateurs, diffusion des procédures, homogénéisation des réponses." },
  { icon: Layers, t: "Guider les intégrations", d: "API, connecteurs, documentation technique : NET assiste partenaires et développeurs." },
];

const BENEFICES = [
  { icon: Clock, t: "Gagner du temps" },
  { icon: Zap, t: "Réduire la charge mentale" },
  { icon: Search, t: "Finir les recherches interminables" },
  { icon: CheckCircle2, t: "Diminuer les erreurs" },
  { icon: Users, t: "Homogénéiser les procédures" },
  { icon: GraduationCap, t: "Accélérer la formation" },
];

const SOUVERAIN = [
  { icon: Landmark, t: "Modèles européens", d: "Prioritairement Mistral, champion français de l'IA." },
  { icon: Server, t: "Hébergement HDS", d: "Compatible avec les exigences Hébergeur de Données de Santé." },
  { icon: ShieldCheck, t: "RGPD by design", d: "Architecture respectueuse du règlement européen." },
  { icon: UserCheck, t: "Authentification pro", d: "Accès réservé aux professionnels, gouvernance des accès." },
  { icon: Lock, t: "Sécurité respectée", d: "NET ne contourne jamais les mécanismes de sécurité — et ne remplace pas Pro Santé Connect." },
  { icon: Users, t: "Supervision humaine", d: "NET assiste. L'humain décide. Toujours." },
];

export default function NetHome() {
  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC", color: NAVY }} data-testid="page-net">
      <SEO
        title="NET : l'assistant IA souverain des soignants · MetaConnect"
        description="Le collaborateur numérique souverain de MetaConnect : réponses métier instantanées, recherche documentaire, synthèses. IA Mistral, hébergement HDS, RGPD."
        canonical="/net"
        image="/net-mascotte.png"
        schema={[
          NET_SOFTWARE_SCHEMA,
          faqSchema(NET_FAQ),
          breadcrumb([{ name: "Accueil", url: "/" }, { name: "NET — Assistant IA", url: "/net" }]),
        ]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-16 lg:pt-40 lg:pb-20" style={{ background: "#ffffff" }} data-testid="net-hero">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1fr_380px] gap-10 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px]"
              style={{ background: "rgba(20,184,166,0.10)", border: "1px solid rgba(20,184,166,0.30)", color: TEAL }}
              data-testid="net-hero-badge"
            >
              <Sparkles size={13} />
              <span style={{ fontWeight: 600 }}>Nouveau</span>
              <span style={{ color: "rgba(15,43,91,0.40)" }}>·</span>
              <span style={{ color: NAVY }}>IA souveraine · Mistral · HDS</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-[-0.03em]" style={{ color: NAVY, fontWeight: 600 }} data-testid="net-hero-title">
              NET, le collaborateur numérique <span style={{ color: TEAL }}>souverain</span> des professionnels de santé
            </h1>
            <p className="mt-6 text-[16px] lg:text-[18px] leading-relaxed max-w-2xl" style={{ color: "#64748B" }}>
              NET vous fait gagner du temps sur les tâches numériques — recherche, synthèses, préparation
              de messages — tout en laissant <strong style={{ color: NAVY }}>systématiquement les décisions à l'humain</strong>.
              Ce n'est pas un chatbot grand public : c'est un assistant professionnel intégré à l'écosystème
              MetaConnect, accessible directement depuis OhMyVue.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/demo"
                data-testid="net-cta-demo"
                className="inline-flex items-center gap-2 transition hover:-translate-y-0.5"
                style={{ background: NAVY, color: "#ffffff", borderRadius: 999, padding: "13px 28px", fontSize: 14.5, fontWeight: 600, boxShadow: "0 12px 28px -12px rgba(15,43,91,0.5)" }}
              >
                Planifier une démo <ArrowRight size={16} />
              </Link>
              <a
                href="https://ohmyvue.fr"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="net-cta-ohmyvue"
                className="inline-flex items-center gap-2 transition hover:-translate-y-0.5"
                style={{ background: "#ffffff", color: NAVY, border: "1px solid #CBD5E1", borderRadius: 999, padding: "12px 26px", fontSize: 14.5, fontWeight: 600 }}
              >
                Découvrir NET dans OhMyVue
              </a>
            </div>
            <p className="mt-5 text-[13px] font-medium" style={{ color: TEAL }}>
              « NET assiste. L'humain décide. »
            </p>
          </div>
          <div className="relative hidden lg:block" data-testid="net-hero-mascotte">
            <div className="absolute inset-0 rounded-full blur-3xl" style={{ background: "rgba(20,184,166,0.14)" }} aria-hidden="true" />
            <img
              src="/net-mascotte.png"
              alt="NET, la mascotte de l'assistant IA souverain de MetaConnect"
              className="relative w-full max-w-[340px] mx-auto drop-shadow-2xl"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* CE QUE NET EST / N'EST PAS */}
      <section className="py-16 lg:py-20 px-5 lg:px-8" data-testid="net-est-nest-pas">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          <div className="p-8 rounded-2xl" style={{ background: "#ffffff", border: "0.5px solid #E8EDF5" }}>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: TEAL }}>
              <CheckCircle2 size={14} /> Ce que NET est
            </div>
            <ul className="mt-5 space-y-3">
              {[
                "Un assistant professionnel réservé aux acteurs de la santé",
                "Un collaborateur numérique intégré à OhMyVue et MetaConnect",
                "Une IA souveraine : Mistral en priorité, hébergement compatible HDS",
                "Un accélérateur du quotidien : recherche, synthèses, préparation",
              ].map((x) => (
                <li key={x} className="flex items-start gap-3 text-[14.5px] leading-relaxed" style={{ color: "#475569" }}>
                  <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: TEAL }} /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-8 rounded-2xl" style={{ background: NAVY }}>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: SKY }}>
              <Lock size={14} /> Ce que NET n'est pas
            </div>
            <ul className="mt-5 space-y-3">
              {[
                "Un chatbot grand public",
                "Un logiciel métier ou un dossier patient",
                "Une messagerie, un agenda, un ERP ou un CRM",
                "Un outil qui décide à votre place",
              ].map((x) => (
                <li key={x} className="flex items-start gap-3 text-[14.5px] leading-relaxed" style={{ color: "#B8D4E8" }}>
                  <span className="shrink-0 mt-2 w-1.5 h-1.5 rounded-full" style={{ background: SKY }} /> {x}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-[13px] leading-relaxed" style={{ color: "#93B4D4" }}>
              NET vient <strong style={{ color: "#ffffff" }}>au-dessus des logiciels existants</strong> pour
              les rendre plus simples à utiliser — jamais pour les remplacer.
            </p>
          </div>
        </div>
      </section>

      {/* MISSIONS */}
      <section className="py-16 lg:py-20 px-5 lg:px-8" style={{ background: "#ffffff" }} data-testid="net-missions">
        <div className="max-w-6xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: TEAL }}>Les missions de NET</div>
          <h2 className="font-display text-2xl lg:text-3xl mt-3 max-w-2xl" style={{ color: NAVY, fontWeight: 600 }}>
            Un assistant qui prépare, pour un professionnel qui valide
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MISSIONS.map(({ icon: Icon, t, d }) => (
              <div key={t} className="p-6 rounded-xl transition hover:-translate-y-1" style={{ background: "#F8FAFC", border: "0.5px solid #E8EDF5" }}>
                <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: "rgba(20,184,166,0.10)", border: "0.5px solid rgba(20,184,166,0.25)" }}>
                  <Icon size={20} style={{ color: TEAL }} />
                </div>
                <div className="mt-4 font-semibold text-[16px]" style={{ color: NAVY }}>{t}</div>
                <p className="mt-1.5 text-[13.5px] leading-relaxed" style={{ color: "#64748B" }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BÉNÉFICES + MASCOTTE */}
      <section className="py-16 lg:py-20 px-5 lg:px-8" data-testid="net-benefices">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[300px_1fr] gap-10 items-center">
          <img
            src="/net-mascotte.png"
            alt="La mascotte NET présente les bénéfices"
            className="w-56 lg:w-full max-w-[280px] mx-auto drop-shadow-xl"
            loading="lazy"
          />
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: TEAL }}>Les grands bénéfices</div>
            <h2 className="font-display text-2xl lg:text-3xl mt-3" style={{ color: NAVY, fontWeight: 600 }}>
              Moins de friction numérique, plus de temps pour vos patients et vos clients
            </h2>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {BENEFICES.map(({ icon: Icon, t }) => (
                <div key={t} className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "#ffffff", border: "0.5px solid #E8EDF5" }}>
                  <Icon size={18} style={{ color: TEAL }} className="shrink-0" />
                  <span className="text-[13.5px] font-medium" style={{ color: NAVY }}>{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* IA SOUVERAINE & SÉCURITÉ */}
      <section className="py-16 lg:py-20 px-5 lg:px-8" style={{ background: NAVY }} data-testid="net-souverainete">
        <div className="max-w-6xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: SKY }}>IA souveraine · Sécurité</div>
          <h2 className="font-display text-2xl lg:text-3xl mt-3 max-w-2xl" style={{ fontWeight: 600, color: "#ffffff" }}>
            Une gouvernance 100 % française, pensée pour la santé
          </h2>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed" style={{ color: "#93B4D4" }}>
            NET est opéré par MetaConnect avec des modèles européens — prioritairement
            <strong className="text-white"> Mistral</strong> — sur des infrastructures compatibles avec les
            exigences <strong className="text-white">HDS</strong>. Vos données restent gouvernées, vos accès
            maîtrisés, vos décisions humaines.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SOUVERAIN.map(({ icon: Icon, t, d }) => (
              <div key={t} className="p-6 rounded-xl" style={{ background: "rgba(255,255,255,0.05)", border: "0.5px solid rgba(255,255,255,0.10)" }}>
                <Icon size={20} style={{ color: TEAL }} />
                <div className="mt-3 font-semibold text-[15px] text-white">{t}</div>
                <p className="mt-1 text-[13px] leading-relaxed" style={{ color: "#93B4D4" }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KNOWLEDGE HUB */}
      <section className="py-16 lg:py-20 px-5 lg:px-8" style={{ background: "#ffffff" }} data-testid="net-knowledge-hub">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: TEAL }}>Knowledge Hub</div>
            <h2 className="font-display text-2xl lg:text-3xl mt-3" style={{ color: NAVY, fontWeight: 600 }}>
              Des réponses construites sur une base documentaire maîtrisée
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "#64748B" }}>
              NET ne répond pas « au hasard » : ses réponses sont prioritairement construites à partir
              d'une base documentaire que vous maîtrisez — documentation MetaConnect et partenaires,
              procédures internes, FAQ, guides, fiches produits, notices, contenus de formation.
            </p>
            <Link to="/demo" className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold transition hover:gap-3" style={{ color: TEAL }} data-testid="net-kh-cta">
              Alimenter votre Knowledge Hub <ArrowRight size={15} />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {["Documentation MetaConnect", "Documentation partenaire", "Procédures internes", "FAQ & guides", "Fiches produits & notices", "Docs techniques & formations"].map((x) => (
              <div key={x} className="flex items-center gap-2.5 p-4 rounded-xl" style={{ background: "#F8FAFC", border: "0.5px solid #E8EDF5" }}>
                <BookOpenCheck size={16} style={{ color: TEAL }} className="shrink-0" />
                <span className="text-[13px] font-medium" style={{ color: NAVY }}>{x}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERSONAS */}
      <section className="py-16 lg:py-20 px-5 lg:px-8" data-testid="net-personas">
        <div className="max-w-6xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: TEAL }}>NET pour votre métier</div>
          <h2 className="font-display text-2xl lg:text-3xl mt-3" style={{ color: NAVY, fontWeight: 600 }}>
            Un collaborateur numérique, cinq univers métier
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {NET_PERSONA_LIST.map((p) => (
              <Link
                key={p.slug}
                to={`/net/${p.slug}`}
                data-testid={`net-persona-${p.slug}`}
                className="group p-6 rounded-xl transition hover:-translate-y-1"
                style={{ background: "#ffffff", border: "0.5px solid #E8EDF5", boxShadow: "0 1px 2px rgba(15,43,91,0.04)" }}
              >
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: TEAL }}>NET pour</div>
                <div className="mt-2 font-display font-semibold text-[19px]" style={{ color: NAVY }}>{p.metier}</div>
                <p className="mt-2 text-[13px] leading-relaxed line-clamp-3" style={{ color: "#64748B" }}>{p.intro}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold transition group-hover:gap-2.5" style={{ color: TEAL }}>
                  Découvrir <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20 px-5 lg:px-8" style={{ background: "#ffffff" }} data-testid="net-faq">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-2xl lg:text-3xl text-center" style={{ color: NAVY, fontWeight: 600 }}>Questions fréquentes sur NET</h2>
          <div className="mt-10 space-y-4">
            {NET_FAQ.map(({ q, a }) => (
              <details key={q} className="group p-5 rounded-xl" style={{ background: "#F8FAFC", border: "0.5px solid #E8EDF5" }}>
                <summary className="cursor-pointer font-semibold text-[15px] list-none flex items-center justify-between gap-4" style={{ color: NAVY }}>
                  {q}
                  <ArrowRight size={15} className="shrink-0 transition group-open:rotate-90" style={{ color: TEAL }} />
                </summary>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "#64748B" }}>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-16 lg:py-24 px-5 lg:px-8 text-center" data-testid="net-final-cta">
        <div className="max-w-2xl mx-auto">
          <img src="/net-mascotte.png" alt="NET vous accompagne" className="w-28 mx-auto drop-shadow-lg" loading="lazy" />
          <h2 className="font-display text-2xl lg:text-4xl mt-6" style={{ color: NAVY, fontWeight: 600 }}>
            Prêt à rencontrer votre collaborateur numérique ?
          </h2>
          <p className="mt-4 text-[15px]" style={{ color: "#64748B" }}>
            Découvrez NET en conditions réelles, avec vos cas d'usage métier.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" className="inline-flex items-center gap-2 transition hover:-translate-y-0.5" style={{ background: NAVY, color: "#ffffff", borderRadius: 999, padding: "13px 30px", fontSize: 14.5, fontWeight: 600 }} data-testid="net-final-cta-demo">
              Planifier une démo <ArrowRight size={16} />
            </Link>
            <Link to="/blog" className="inline-flex items-center gap-2 transition hover:-translate-y-0.5" style={{ background: "#ffffff", color: NAVY, border: "1px solid #CBD5E1", borderRadius: 999, padding: "12px 26px", fontSize: 14.5, fontWeight: 600 }} data-testid="net-final-cta-blog">
              Lire les articles NET
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
